import { ChevronRight, MapPin, Sparkles, Stethoscope } from 'lucide-react';
import { motion } from 'motion/react';

import {
  DASHBOARD_PATIENT_LOCATION_ANCHOR,
  DASHBOARD_PATIENT_RELATIONSHIP_ANCHOR,
  DASHBOARD_PATIENT_REQUESTS_ANCHOR,
  DASHBOARD_PATIENT_VIEWER_ANCHOR,
} from '@/app/hints';
import {
  POLICY_RELATIONSHIP_DESCRIPTION,
  POLICY_RELATIONSHIP_LABEL,
  formatPatientLocationLabel,
  type PatientNavigatorItemData,
} from '@/features/patient-navigator';
import { cn } from '@/lib/utils';
import { formatRelativeAgeLabel } from '@/shared/lib';
import { Badge } from '@/shared/ui/shadcn/badge';
import { useHintAnchor } from '@/shared/ui/primitives';

interface PatientQueueCardProps {
  patient: PatientNavigatorItemData;
  index: number;
  onSelect: () => void;
}

function getInitials(fullName: string): string {
  return fullName
    .split(' ')
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// Inline status-row label for a priority bucket (e.g. "1 urgent request",
// "3 routine requests"). Kept local to the card because it pairs 1:1 with the
// visible chip; promote to a shared formatter only when a second surface needs
// the same phrasing.
function formatPriorityRequests(count: number, priority: 'urgent' | 'routine'): string {
  const noun = count === 1 ? 'request' : 'requests';
  return `${count} ${priority} ${noun}`;
}

const RELATIONSHIP_STYLE: Record<
  PatientNavigatorItemData['policyRelationship'],
  { dotColor: string; textColor: string }
> = {
  actionable: {
    dotColor: 'bg-green-400',
    textColor: 'text-green-400',
  },
  licensed: {
    dotColor: 'bg-blue-400',
    textColor: 'text-blue-400',
  },
  unlicensed: {
    dotColor: 'bg-orange-400',
    textColor: 'text-orange-400',
  },
};

export function PatientQueueCard({ patient, index, onSelect }: PatientQueueCardProps) {
  const ageLabel = formatRelativeAgeLabel(patient.oldestRequestAt);
  const hasUrgent = patient.urgentCount > 0;
  const hasRoutine = patient.routineCount > 0;
  const hasActiveVisit = patient.viewerCount > 0;
  const hasOpenRequests = hasUrgent || hasRoutine;
  const relationshipStyle = RELATIONSHIP_STYLE[patient.policyRelationship];
  const relationshipLabel = POLICY_RELATIONSHIP_LABEL[patient.policyRelationship];
  const relationshipDescription = POLICY_RELATIONSHIP_DESCRIPTION[patient.policyRelationship];

  const patientLocationLabel = formatPatientLocationLabel(patient.patientCurrentState);
  const practiceAreas =
    patient.patientPracticeAreas.length > 0 ? patient.patientPracticeAreas.join(', ') : null;

  // Bind hint anchors only on the first card so the dashboard onboarding
  // sequence has a single, predictable spotlight target. The viewer anchor
  // additionally gates on `hasActiveVisit`, so the corresponding hint stays
  // dormant on quiet queues and the engine moves to the next eligible hint.
  const isFirstCard = index === 0;
  const relationshipAnchorRef = useHintAnchor(
    isFirstCard ? DASHBOARD_PATIENT_RELATIONSHIP_ANCHOR : undefined
  );
  const viewerAnchorRef = useHintAnchor(
    isFirstCard && hasActiveVisit ? DASHBOARD_PATIENT_VIEWER_ANCHOR : undefined
  );
  const locationAnchorRef = useHintAnchor(
    isFirstCard ? DASHBOARD_PATIENT_LOCATION_ANCHOR : undefined
  );
  const requestsAnchorRef = useHintAnchor(
    isFirstCard && hasOpenRequests ? DASHBOARD_PATIENT_REQUESTS_ANCHOR : undefined
  );

  return (
    <motion.button
      type="button"
      onClick={onSelect}
      className={cn(
        'group flex w-full items-center gap-4 rounded-xl p-4 text-left',
        'border border-white/8 bg-white/[0.02]',
        'transition-all duration-200',
        'hover:border-white/20 hover:bg-white/[0.05] hover:shadow-lg hover:shadow-black/10',
        'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-white/20'
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        duration: 0.2,
        delay: Math.min(index * 0.02, 0.2),
      }}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
          hasUrgent
            ? 'bg-red-500/20 text-red-300'
            : hasRoutine
              ? 'bg-amber-500/20 text-amber-300'
              : 'bg-white/10 text-white/70'
        )}
        aria-hidden="true"
      >
        {getInitials(patient.fullName)}
      </div>

      {/* Patient info */}
      <div className="min-w-0 flex-1">
        {/* Name row */}
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-white">{patient.fullName}</span>
          {hasActiveVisit ? (
            <span ref={viewerAnchorRef} className="flex shrink-0 items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-green-400" aria-hidden="true" />
              <span className="text-[10px] font-medium text-green-400">Being seen</span>
            </span>
          ) : null}
        </div>

        {/* Status row: relationship + requests + age */}
        <div className="mt-1 flex items-center gap-2">
          <span
            ref={relationshipAnchorRef}
            className="flex shrink-0 items-center gap-1"
            title={relationshipDescription}
          >
            <span
              className={cn('h-1.5 w-1.5 rounded-full', relationshipStyle.dotColor)}
              aria-hidden="true"
            />
            <span className={cn('text-[11px] font-medium', relationshipStyle.textColor)}>
              {relationshipLabel}
            </span>
          </span>

          {hasOpenRequests ? (
            <span ref={requestsAnchorRef} className="flex shrink-0 items-center gap-2">
              <span className="text-[10px] text-white/25" aria-hidden="true">
                &middot;
              </span>
              {hasUrgent ? (
                <span className="text-[11px] font-medium text-red-400">
                  {formatPriorityRequests(patient.urgentCount, 'urgent')}
                </span>
              ) : null}
              {hasUrgent && hasRoutine ? (
                <span className="text-[10px] text-white/25" aria-hidden="true">
                  &middot;
                </span>
              ) : null}
              {hasRoutine ? (
                <span className="text-[11px] font-medium text-orange-400">
                  {formatPriorityRequests(patient.routineCount, 'routine')}
                </span>
              ) : null}
            </span>
          ) : null}

          {ageLabel !== null ? (
            <>
              <span className="text-[10px] text-white/25" aria-hidden="true">
                &middot;
              </span>
              <span className="text-[11px] font-medium text-white/40">{ageLabel}</span>
            </>
          ) : null}
        </div>

        {/* Metadata row: state + practice areas */}
        <div className="mt-1.5 flex items-center gap-3">
          <span
            ref={locationAnchorRef}
            className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-white/40"
          >
            <MapPin className="h-3 w-3 text-white/30" aria-hidden="true" />
            {patientLocationLabel}
          </span>

          {practiceAreas !== null ? (
            <span className="flex items-center gap-1 truncate text-[11px] font-medium text-white/40">
              <Stethoscope className="h-3 w-3 shrink-0 text-white/30" aria-hidden="true" />
              <span className="truncate">{practiceAreas}</span>
            </span>
          ) : null}
        </div>
      </div>

      {/* Karma reward — sole right-side chip; request counts now live inline
          in the status row to eliminate prior duplication. */}
      {patient.totalKarmaOffer > 0 ? (
        <Badge
          variant="outline"
          className="h-6 min-w-6 shrink-0 gap-0.5 rounded-full border-yellow-500/30 bg-yellow-500/10 px-1.5 text-[11px] text-yellow-300"
          aria-label={`${patient.totalKarmaOffer} karma points available`}
        >
          <Sparkles className="h-3 w-3" aria-hidden="true" />
          {patient.totalKarmaOffer}
        </Badge>
      ) : null}

      {/* Hover chevron */}
      <ChevronRight
        className="h-4 w-4 shrink-0 text-white/30 opacity-0 transition-opacity group-hover:opacity-100"
        aria-hidden="true"
      />
    </motion.button>
  );
}
